import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { useQueryClient } from 'react-query';
import { queryPredicate, useQueryDevices } from '@frontend/api-phone-sync';
import { UsersApi } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { getFullName } from '@frontend/name';
import { useDeviceConnectionNotification } from '@frontend/notifications';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useWebsocketEventSubscription } from '@frontend/websocket';

export const useWebsocketDeviceConnectionSubscription = () => {
  const { setPhoneConfig, phoneConfig } = usePhoneConfigShallowStore('setPhoneConfig', 'phoneConfig');
  const { selectedLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasDeviceConnectionChangeFlag = getFeatureFlagValue('device-connection-change');

  const { data: devices } = useQueryDevices(selectedLocationIds, {
    select: (data) => data?.filter((device) => device.availableInContext),
  });

  const user = getUser();

  const { create } = useDeviceConnectionNotification();
  useWebsocketEventSubscription(NotificationType.DEVICE_CONNECTION, async (payload) => {
    const device = devices?.find((d) => d.sipProfileId === payload.params.sipProfileId);
    if (
      phoneConfig?.activeUserId === payload.params.activeUserId &&
      phoneConfig?.sipProfileId === payload.params.sipProfileId
    ) {
      /**
       * Don't do anything if the device is already set in the phone config.
       * The event likely originated from the same client
       */
    } else if (device && payload.params.connected) {
      setPhoneConfig({ ...device, activeUserId: payload.params.activeUserId });
    } else if (device && !payload.params.connected) {
      if (
        hasDeviceConnectionChangeFlag &&
        payload.params.activeUserId &&
        user?.userID !== payload.params.activeUserId
      ) {
        try {
          const activeUser = await queryClient.fetchQuery({
            queryKey: ['user', payload.params.activeUserId],
            queryFn: () => {
              return UsersApi.list.getUserProfileByUserID(payload.params.activeUserId ?? '');
            },
          });

          create({
            type: 'device-connection',
            payload: { ...payload.params, deviceName: device?.name, activeUserName: getFullName(activeUser) },
            timestamp: new Date().toISOString(),
            id: payload.id,
            state: {
              paused: false,
              status: 'unread',
            },
          });
        } catch {
          create({
            type: 'device-connection',
            payload: { ...payload.params, deviceName: device?.name },
            timestamp: new Date().toISOString(),
            id: payload.id,
            state: {
              paused: false,
              status: 'unread',
            },
          });
        }
      }

      setPhoneConfig(undefined);
    } else {
      console.warn('Device not found in the current context', payload.params.sipProfileId);
    }
    queryClient.invalidateQueries({
      predicate: queryPredicate.listDevices,
    });
  });
};
